/**
 * PostMessageAnimationLayer - Renders the current animation step
 *
 * Responsible for:
 * - Rendering toast, item-appear, and educational animation steps
 * - Triggering stat mutations (addCash, addInvestments, addHappiness) when
 *   toast animations complete — stats are applied here, not upfront
 * - Triggering addItem when item-appear animation is dismissed
 * - Setting revealedItemId so AnimationBackdrop can render the item
 *   inside the dark overlay for a seamless fade-out
 * - Triggering unlockAchievement when achievement toasts complete
 * - Calling advanceAnimationStep to move to the next step in the sequence
 */

import { useCallback, useEffect } from "react";
import { useGameStore } from "@/state/gameStore";
import { useCurrentAnimationStep, useCurrentMessage } from "@/state/selectors";
import { ToastMessages } from "@/components/screens/PlayingScreen/components/PostMessageAnimationLayer/components/ToastMessages";
import EducationalMessageDisplay from "@/components/screens/PlayingScreen/components/PostMessageAnimationLayer/components/EducationalMessageDisplay";
import ItemAppearAnimation from "@/components/screens/PlayingScreen/components/PostMessageAnimationLayer/components/ItemAppearAnimation";
import type { ToastMessage } from "@/types/state";

export function PostMessageAnimationLayer() {
    const currentStep = useCurrentAnimationStep();
    const currentMessage = useCurrentMessage();

    const addCash = useGameStore((state) => state.addCash);
    const addInvestments = useGameStore((state) => state.addInvestments);
    const addHappiness = useGameStore((state) => state.addHappiness);
    const addItem = useGameStore((state) => state.addItem);
    const unlockAchievement = useGameStore((state) => state.unlockAchievement);
    const advanceAnimationStep = useGameStore((state) => state.advancePostMessageAnimationStep);
    const setRevealedItemId = useGameStore((state) => state.setRevealedItemId);
    const clearRevealedItemId = useGameStore((state) => state.clearRevealedItemId);

    const historyTitle = currentMessage?.transaktions_name ?? currentMessage?.name ?? "Transaktion";

    // Clear the revealed item when a non-item step starts,
    // e.g. item-appear → educational in the same sequence.
    useEffect(() => {
        if (currentStep && currentStep.type !== "item-appear") {
            clearRevealedItemId();
        }
    }, [currentStep, clearRevealedItemId]);

    const handleToastsComplete = useCallback(
        (toasts: ToastMessage[]) => {
            for (const toast of toasts) {
                switch (toast.type) {
                    case "cash":
                        addCash(toast.change, historyTitle);
                        break;
                    case "depot":
                        addInvestments(toast.change, historyTitle);
                        break;
                    case "happiness":
                        addHappiness(toast.change);
                        break;
                    case "earnings":
                        addInvestments(toast.change, "Rendite");
                        break;
                    case "achievement":
                        unlockAchievement(toast.achievement.documentId);
                        break;
                    case "item":
                        break;
                }
            }

            advanceAnimationStep();
        },
        [
            addCash,
            addInvestments,
            addHappiness,
            unlockAchievement,
            advanceAnimationStep,
            historyTitle,
            currentMessage?.type,
        ],
    );

    const handleItemClose = useCallback(() => {
        if (currentStep?.type === "item-appear") {
            // 1. Add item to inventory (appears in RoomBackground)
            addItem(currentStep.item.documentId);
            // 2. Tell AnimationBackdrop to render this item inside the dark overlay
            setRevealedItemId(currentStep.item.documentId);
            // 3. Defer step advance to the next frame so AnimationBackdrop
            //    picks up revealedItemId BEFORE darkOverlayVisible goes false.
            //    This guarantees the item is inside the backdrop when exit starts.
            requestAnimationFrame(() => {
                advanceAnimationStep();
            });
        } else {
            advanceAnimationStep();
        }
    }, [currentStep, addItem, setRevealedItemId, advanceAnimationStep]);

    if (!currentStep) return null;

    switch (currentStep.type) {
        case "toasts":
            return (
                <ToastMessages
                    toasts={currentStep.toasts}
                    onComplete={() => handleToastsComplete(currentStep.toasts)}
                />
            );

        case "item-appear":
            return <ItemAppearAnimation item={currentStep.item} onClose={handleItemClose} />;

        case "educational":
            return (
                <EducationalMessageDisplay message={currentStep.educationalMessage} onClose={advanceAnimationStep} />
            );

        default:
            return null;
    }
}